/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { CheckIcon } from 'lucide-react'
import {
  type ComponentProps,
  cloneElement,
  createContext,
  type HTMLAttributes,
  isValidElement,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'

import { cn } from '@/lib/utils'

type StepsContextValue = {
  count: number
  step: number
  completed: boolean
  linear: boolean
  setStep: (next: number) => void
  goNext: () => void
  goPrev: () => void
  listId: string
}

const StepsContext = createContext<StepsContextValue | null>(null)

function useStepsContext(component: string): StepsContextValue {
  const ctx = useContext(StepsContext)
  if (!ctx) {
    throw new Error(`${component} must be used within Steps.Root`)
  }
  return ctx
}

function clampStep(value: number, count: number) {
  if (count <= 0) {
    return 0
  }
  return Math.min(Math.max(0, value), count - 1)
}

export type StepsRootProps = {
  /** Number of steps (valid indices are `0` … `count - 1`). */
  count: number
  /** Controlled active step index. */
  step?: number
  /** Uncontrolled initial step (used when `step` is omitted). */
  defaultStep?: number
  onStepChange?: (details: { step: number }) => void
  /**
   * When true, step headers only navigate backward or to the next step (no skipping ahead).
   * @default false
   */
  linear?: boolean
  /**
   * When true, `Steps.Content` panels are hidden and `Steps.CompletedContent` is shown instead.
   */
  completed?: boolean
} & Omit<ComponentProps<'div'>, 'onChange'>

export function StepsRoot({
  count,
  step: stepProp,
  defaultStep = 0,
  onStepChange,
  linear = false,
  completed = false,
  className,
  children,
  ...rest
}: StepsRootProps) {
  const listId = useId()
  const isControlled = stepProp !== undefined
  const [uncontrolledStep, setUncontrolledStep] = useState(() => clampStep(defaultStep, count))

  const step = clampStep(isControlled ? stepProp : uncontrolledStep, count)

  useEffect(() => {
    if (!isControlled) {
      setUncontrolledStep((prev) => clampStep(prev, count))
    }
  }, [count, isControlled])

  const setStep = useCallback(
    (nextRaw: number) => {
      const next = clampStep(nextRaw, count)
      if (!isControlled) {
        setUncontrolledStep(next)
      }
      if (next !== step) {
        onStepChange?.({ step: next })
      }
    },
    [count, isControlled, onStepChange, step]
  )

  const goNext = useCallback(() => {
    setStep(step + 1)
  }, [setStep, step])

  const goPrev = useCallback(() => {
    setStep(step - 1)
  }, [setStep, step])

  const value = useMemo<StepsContextValue>(
    () => ({
      count,
      step,
      completed,
      linear,
      setStep,
      goNext,
      goPrev,
      listId,
    }),
    [completed, count, goNext, goPrev, linear, listId, setStep, step]
  )

  return (
    <StepsContext.Provider value={value}>
      <div className={cn('flex w-full flex-col gap-6', className)} data-slot="steps-root" {...rest}>
        {children}
      </div>
    </StepsContext.Provider>
  )
}

export type StepsListProps = ComponentProps<'div'>

export function StepsList({ className, children, ...rest }: StepsListProps) {
  const { listId } = useStepsContext('Steps.List')
  return (
    <div
      aria-orientation="horizontal"
      className={cn('flex w-full flex-wrap items-start gap-y-3', className)}
      data-slot="steps-list"
      id={listId}
      role="tablist"
      {...rest}
    >
      {children}
    </div>
  )
}

export type StepsItemProps = {
  index: number
  title?: ReactNode
  description?: ReactNode
  completedIcon?: ReactNode
  icon?: ReactNode
} & Omit<ComponentProps<'div'>, 'title'>

export function StepsItem(props: StepsItemProps) {
  const { index, title, description, completedIcon, icon, className, children, ...rest } = props
  const { step, count, linear, setStep, listId } = useStepsContext('Steps.Item')

  const isComplete = index < step
  const isCurrent = index === step
  const isFuture = index > step

  const canNavigateTo = useCallback(() => {
    if (linear) {
      return index <= step + 1
    }
    return true
  }, [index, linear, step])

  const handleActivate = () => {
    if (!canNavigateTo()) {
      return
    }
    setStep(index)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleActivate()
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault()
      setStep(Math.min(step + 1, count - 1))
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault()
      setStep(Math.max(step - 1, 0))
    }
  }

  const showSeparator = index < count - 1

  const indicator = isComplete ? (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground text-sm">
      {completedIcon ?? <CheckIcon aria-hidden className="size-4" />}
    </span>
  ) : isCurrent ? (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-primary bg-background font-semibold text-primary text-sm">
      {icon ?? index + 1}
    </span>
  ) : (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted/40 font-medium text-muted-foreground text-sm',
        isFuture && linear && 'opacity-60'
      )}
    >
      {icon ?? index + 1}
    </span>
  )

  const labelId = `${listId}-step-${index}-label`
  const descriptionId = `${listId}-step-${index}-description`

  return (
    <div className={cn('flex min-w-0 flex-1 items-center gap-2', className)} data-slot="steps-item" {...rest}>
      <button
        aria-controls={`${listId}-panel-${index}`}
        aria-current={isCurrent ? 'step' : undefined}
        aria-describedby={description ? descriptionId : undefined}
        aria-selected={isCurrent}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-3 rounded-md border-0 bg-transparent p-1 text-left outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          canNavigateTo() ? 'cursor-pointer hover:bg-muted/50' : 'cursor-not-allowed opacity-70'
        )}
        disabled={!canNavigateTo()}
        id={labelId}
        onClick={handleActivate}
        onKeyDown={handleKeyDown}
        role="tab"
        type="button"
      >
        {indicator}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          {title ? (
            <span
              className={cn(
                'font-medium text-sm leading-tight',
                isCurrent ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {title}
            </span>
          ) : null}
          {description ? (
            <span className="text-muted-foreground text-xs leading-snug" id={descriptionId}>
              {description}
            </span>
          ) : null}
          {children}
        </span>
      </button>
      {showSeparator ? (
        <div
          aria-hidden
          className={cn('hidden h-0.5 min-w-[1.5rem] flex-1 bg-border md:block', isComplete && 'bg-primary/40')}
        />
      ) : null}
    </div>
  )
}

export type StepsContentProps = {
  index: number
} & HTMLAttributes<HTMLDivElement>

export function StepsContent({ index, className, children, ...rest }: StepsContentProps) {
  const { step, completed, listId } = useStepsContext('Steps.Content')
  if (completed || step !== index) {
    return null
  }
  return (
    <div
      aria-labelledby={`${listId}-step-${index}-label`}
      className={cn('w-full', className)}
      data-slot="steps-content"
      id={`${listId}-panel-${index}`}
      role="tabpanel"
      {...rest}
    >
      {children}
    </div>
  )
}

export type StepsCompletedContentProps = HTMLAttributes<HTMLDivElement>

export function StepsCompletedContent({ className, children, ...rest }: StepsCompletedContentProps) {
  const { completed } = useStepsContext('Steps.CompletedContent')
  if (!completed) {
    return null
  }
  return (
    <div className={cn('w-full', className)} data-slot="steps-completed-content" {...rest}>
      {children}
    </div>
  )
}

type TriggerProps = {
  asChild?: boolean
} & ComponentProps<'button'>

function mergeTriggerClick(
  child: ReactElement<{ onClick?: (e: MouseEvent) => void }>,
  handler: (e: MouseEvent) => void
) {
  const original = child.props.onClick
  return (e: MouseEvent) => {
    original?.(e)
    if (!e.defaultPrevented) {
      handler(e)
    }
  }
}

export function StepsNextTrigger({ asChild, onClick, disabled, children, ...rest }: TriggerProps) {
  const { step, count, goNext } = useStepsContext('Steps.NextTrigger')
  const atEnd = count <= 0 || step >= count - 1

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (!event.defaultPrevented) {
      goNext()
    }
  }

  if (asChild) {
    if (!isValidElement(children)) {
      throw new Error('Steps.NextTrigger with asChild expects a single React element child.')
    }
    const child = children as ReactElement<{ onClick?: (e: MouseEvent) => void; disabled?: boolean }>
    return cloneElement(child, {
      ...rest,
      disabled: disabled ?? child.props.disabled ?? atEnd,
      onClick: mergeTriggerClick(child, handleClick as (e: MouseEvent) => void),
    })
  }

  return (
    <button disabled={disabled ?? atEnd} onClick={handleClick} type="button" {...rest}>
      {children ?? 'Next'}
    </button>
  )
}

export function StepsPrevTrigger({ asChild, onClick, disabled, children, ...rest }: TriggerProps) {
  const { step, goPrev } = useStepsContext('Steps.PrevTrigger')
  const atStart = step <= 0

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (!event.defaultPrevented) {
      goPrev()
    }
  }

  if (asChild) {
    if (!isValidElement(children)) {
      throw new Error('Steps.PrevTrigger with asChild expects a single React element child.')
    }
    const child = children as ReactElement<{ onClick?: (e: MouseEvent) => void; disabled?: boolean }>
    return cloneElement(child, {
      ...rest,
      disabled: disabled ?? child.props.disabled ?? atStart,
      onClick: mergeTriggerClick(child, handleClick as (e: MouseEvent) => void),
    })
  }

  return (
    <button disabled={disabled ?? atStart} onClick={handleClick} type="button" {...rest}>
      {children ?? 'Previous'}
    </button>
  )
}

export type StepsIndicatorProps = {
  index: number
  completedIcon?: ReactNode
  icon?: ReactNode
} & ComponentProps<'div'>

export function StepsIndicator({ index, completedIcon, icon, className, ...rest }: StepsIndicatorProps) {
  const { step } = useStepsContext('Steps.Indicator')
  const isComplete = index < step
  const isCurrent = index === step

  const body = isComplete ? (
    <span className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
      {completedIcon ?? <CheckIcon aria-hidden className="size-4" />}
    </span>
  ) : isCurrent ? (
    <span className="flex size-8 items-center justify-center rounded-full border-2 border-primary bg-background font-semibold text-primary text-sm">
      {icon ?? index + 1}
    </span>
  ) : (
    <span className="flex size-8 items-center justify-center rounded-full border border-border bg-muted/40 font-medium text-muted-foreground text-sm">
      {icon ?? index + 1}
    </span>
  )

  return (
    <div className={cn('flex items-center justify-center', className)} data-slot="steps-indicator" {...rest}>
      {body}
    </div>
  )
}

/** Chakra-style namespace for `Steps.Root`, `Steps.List`, … */
export const Steps = {
  Root: StepsRoot,
  List: StepsList,
  Item: StepsItem,
  Content: StepsContent,
  CompletedContent: StepsCompletedContent,
  NextTrigger: StepsNextTrigger,
  PrevTrigger: StepsPrevTrigger,
  Indicator: StepsIndicator,
}
